// İSG uzmanı portalı — Eğitim & Test (Training) erişim sondası (route guard UX).
//
// Asıl yetki backend'dedir: her Eğitim ucu `[ModuleAction("training", …)]` taşır; grant'ı
// olmayan principal → 403 (api-contract §16). Bu sonda yalnız UX görünürlüğüdür — hafif bir
// liste probu (`GET /trainings?page=1&pageSize=1`) çeker:
//   - 2xx  → erişim var (granted) → Eğitim kabuğu + menü görünür
//   - 403  → yetki yok (forbidden) → gizli menü + 403 sayfası
//   - diğer (404/503/ağ) → servis/veri kullanılamıyor (unavailable) → yeniden dene
//
// Taşeron (§13) / İş İzni (§14) sondalarıyla aynı desendir; iş modülü olarak AYRIDIR. Gerçek
// modül Value = `training` (küçük harf; §16) — probun bağladığı uç `list` aksiyonudur; yetkisiz
// principal buton/route göremez.

import { ApiError, trainingApi } from '@isgportal/shared';
import { useQuery } from '@tanstack/react-query';
import { trainingKeys } from './trainingKeys';

export type TrainingAccessStatus = 'loading' | 'granted' | 'forbidden' | 'unavailable';

export interface TrainingAccess {
  status: TrainingAccessStatus;
  /** `unavailable` durumunda yeniden denemek için. */
  refetch: () => void;
}

/**
 * Eğitim erişimini backend sondasıyla belirler. Oturum açık (RequireAuth altı) varsayılır.
 * 4xx'te retry yok (anında sonuç); 403 (yetki yok) ≠ 404/503 (servis/veri) ayrımı korunur.
 */
export function useTrainingAccess(): TrainingAccess {
  const query = useQuery({
    queryKey: trainingKeys.access(),
    queryFn: () => trainingApi.listTrainings({ page: 1, pageSize: 1 }),
    retry: false,
    staleTime: 5 * 60_000,
  });

  let status: TrainingAccessStatus;
  if (query.isPending) {
    status = 'loading';
  } else if (query.isSuccess) {
    status = 'granted';
  } else if (query.error instanceof ApiError && query.error.statusCode === 403) {
    status = 'forbidden';
  } else {
    status = 'unavailable';
  }

  return { status, refetch: () => void query.refetch() };
}
